import { useState } from 'react'
import { AidStation, PlanItem } from '../../types'
import { AddPlanItemDialog } from './AddPlanItemDialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Plus, Trash2, Flag, Clock } from 'lucide-react'
import { useDeletePlanItem } from '../../hooks/usePlanItems'
import { formatTime } from '../../lib/calculations'

interface AidStationPlanBlockProps {
  aidStation: AidStation
  planItems: PlanItem[]
}

export function AidStationPlanBlock({ aidStation, planItems }: AidStationPlanBlockProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const deleteMutation = useDeletePlanItem()

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this plan item?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const formatNutrition = (item: PlanItem) => {
    const parts = []
    if (item.carbs_g) parts.push(`${item.carbs_g}g carbs`)
    if (item.fluids_ml) parts.push(`${item.fluids_ml}ml fluids`)
    if (item.sodium_mg) parts.push(`${item.sodium_mg}mg sodium`)
    if (item.caffeine_mg) parts.push(`${item.caffeine_mg}mg caffeine`)
    return parts.join(', ')
  }

  // Calculate aid station nutrition totals
  const aidStationTotals = {
    carbs: planItems.reduce((sum, item) => sum + (item.carbs_g || 0), 0),
    fluids: planItems.reduce((sum, item) => sum + (item.fluids_ml || 0), 0),
    sodium: planItems.reduce((sum, item) => sum + (item.sodium_mg || 0), 0),
    caffeine: planItems.reduce((sum, item) => sum + (item.caffeine_mg || 0), 0)
  }

  return (
    <Card className="w-full border-l-4 border-l-orange-600 bg-orange-50 shadow-minimal">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 space-y-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold flex items-center gap-3 text-primary-900">
                <div className="flex items-center justify-center w-10 h-10 bg-orange-600 text-white rounded-xl">
                  <Flag className="h-5 w-5" />
                </div>
                {aidStation.name}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="shrink-0 bg-white hover:bg-orange-100 border-orange-300 text-orange-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Nutrition
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-xl border border-orange-200">
              <div className="text-center">
                <div className="text-xs text-orange-700 uppercase tracking-wide mb-2 font-medium">Distance</div>
                <div className="font-bold text-primary-900 text-lg flex items-center justify-center gap-2">
                  <Flag className="h-4 w-4 text-orange-600" />
                  {aidStation.at_distance_km}km
                </div>
              </div>
              {aidStation.planned_stop_min && aidStation.planned_stop_min > 0 && (
                <div className="text-center">
                  <div className="text-xs text-orange-700 uppercase tracking-wide mb-2 font-medium">Stop Time</div>
                  <div className="font-bold text-primary-900 text-lg flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    {formatTime(aidStation.planned_stop_min)}
                  </div>
                </div>
              )}
            </div>
            
            {aidStation.available && (aidStation.available as string[]).length > 0 && (
              <div className="p-4 bg-white rounded-xl border border-orange-200">
                <div className="text-xs text-orange-700 font-semibold mb-3 uppercase tracking-wide">Available at Station</div>
                <div className="flex flex-wrap gap-2">
                  {(aidStation.available as string[]).map((item, index) => (
                    <Badge key={index} className="text-xs bg-orange-100 text-orange-800 border-0">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {aidStation.notes && (
              <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                <div className="text-xs text-neutral-600 font-semibold mb-2">Notes</div>
                <div className="text-sm text-neutral-700 italic">"{aidStation.notes}"</div>
              </div>
            )}
            
            {planItems.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-orange-100 rounded-xl border border-orange-300">
                {aidStationTotals.carbs > 0 && (
                  <div className="text-center">
                    <div className="text-sm text-orange-800 font-bold">{aidStationTotals.carbs}g</div>
                    <div className="text-xs text-orange-600 font-medium">carbs</div>
                  </div>
                )}
                {aidStationTotals.fluids > 0 && (
                  <div className="text-center">
                    <div className="text-sm text-orange-800 font-bold">{aidStationTotals.fluids}ml</div>
                    <div className="text-xs text-orange-600 font-medium">fluids</div>
                  </div>
                )}
                {aidStationTotals.sodium > 0 && (
                  <div className="text-center">
                    <div className="text-sm text-orange-800 font-bold">{aidStationTotals.sodium}mg</div>
                    <div className="text-xs text-orange-600 font-medium">sodium</div>
                  </div>
                )}
                {aidStationTotals.caffeine > 0 && (
                  <div className="text-center">
                    <div className="text-sm text-orange-800 font-bold">{aidStationTotals.caffeine}mg</div>
                    <div className="text-xs text-orange-600 font-medium">caffeine</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      {planItems.length > 0 && (
        <CardContent className="pt-0">
          <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
            <div className="bg-orange-100 px-4 py-3 border-b border-orange-200">
              <h4 className="text-sm font-bold text-orange-900">Nutrition Plan</h4>
            </div>
            <div className="p-4 space-y-3">
              {planItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl border border-neutral-100 hover:bg-neutral-100 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Badge
                      className={`shrink-0 ${
                        item.type === 'snack'
                          ? 'bg-primary-100 text-primary-800'
                          : item.type === 'drink'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-neutral-100 text-neutral-800'
                      }`}
                    >
                      {item.type}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-semibold text-primary-900">{item.name}</div>
                      {formatNutrition(item) && (
                        <div className="text-sm text-orange-700 font-medium mt-1">{formatNutrition(item)}</div>
                      )}
                      {item.note && (
                        <div className="text-sm text-neutral-600 italic mt-1">"{item.note}"</div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="text-accent-600 hover:text-accent-700 hover:bg-accent-50 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}

      <AddPlanItemDialog
        runId={aidStation.run_id!}
        preSelectedLocation={{
          type: 'aid_station',
          id: aidStation.id,
          label: aidStation.name
        }}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />
    </Card>
  )
}